import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { HttpClient, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ClientProcessingError } from 'app/_errors/client-processing.error';
import { UnknownApplicationError } from 'app/_errors/unknown-application.error';
import { throwError } from 'rxjs';
import { getFileNameFromPath } from 'app/_utils/file-path.utils';

@Injectable()
export class FileService {
  private objectUrlsToRelease = [];

  constructor(private http: HttpClient, private sanitizer: DomSanitizer) {}

  // Delete a file
  public deleteFile(fileId) {
    return this.http
      .delete(environment.apiUrl + 'api/ext-file/' + fileId)
      .pipe(map(this.handleData))
      .pipe(catchError(this.handleError));
  }

  public deleteAllContractorCompetencyFiles(id) {
    return this.http
      .delete(environment.apiUrl + `api/ext-maintenance/maintenance-siteowner-competency/${id}/certificate_files`)
      .pipe(map(this.handleData))
      .pipe(catchError(this.handleError));
  }

  public deleteAllWorkerCompetencyFiles(id: number, isWorker?: boolean) {
    //call different endpoint if deleting competency files as worker
    const workerSegment = isWorker ? '/for-worker' : '';
    const url = `${environment.apiUrl}api/ext-worker-user-competency-site-owner/${id}/certificate-files${workerSegment}`;

    return this.http
      .delete(url)
      .pipe(map(this.handleData))
      .pipe(catchError(this.handleError));
  }

  // Handle Responses
  private handleData(res: HttpResponse<any>) {
    const body = res;
    return body;
  }

  private handleError(error: any) {
    if (error instanceof HttpErrorResponse) {
      return throwError(error);
    }

    if (error instanceof Error) {
      return throwError(
        new ClientProcessingError('The application could not process the server response.', error)
      );
    }

    return throwError(
      new UnknownApplicationError('The application threw an unknown error value.', error)
    );
  }

  public showFile(folder, fileName): void {
    const uri = environment.apiUrl + 'api/ext-file/' + folder + '/' + getFileNameFromPath(fileName);
    this.http.get(uri, { responseType: 'blob' }).subscribe((response) => {
      const fileURL = URL.createObjectURL(response);
      if (response.type.startsWith('image') || response.type.endsWith('pdf')) {
        window.open(fileURL, '_blank');
      } else {
        this.downloadFile(fileURL, fileName);
        window.URL.revokeObjectURL(fileURL);
      }
    });
  }

  public getSiteSessionPhoto(imageName: string): Observable<{ safeUrl: SafeUrl; objectUrl: string }> {
    return this.getPhoto(environment.apiUrl + 'api/ext-file/siteSessionPhotos/' + imageName);
  }

  public getSiteFormResponsePhoto(imageName: string): Observable<{ safeUrl: SafeUrl; objectUrl: string }> {
    return this.getPhoto(environment.apiUrl + 'api/ext-file/siteFormResponse/' + imageName);
  }

  public getAssetImage(imageName: string): Observable<{ safeUrl: SafeUrl; objectUrl: string }> {
    return this.getPhoto(environment.apiUrl + 'api/ext-file/image/' + imageName);
  }

  public getSignatureImage(imageName: string): Observable<{ safeUrl: SafeUrl; objectUrl: string }> {
    if (!imageName) {
      return Observable.of({
        safeUrl: '',
        objectUrl: '',
      });
    } else {
      return this.getPhoto(environment.apiUrl + 'api/ext-file/userSignature/' + imageName);
    }
  }

  public getContractorCertificateImage(imageName: string): Observable<{ safeUrl: SafeUrl; objectUrl: string }> {
    if (!imageName) {
      return Observable.of({
        safeUrl: '',
        objectUrl: '',
      });
    } else {
      return this.getPhoto(environment.apiUrl + 'api/ext-file/mainteanceSiteOwnerCompetencyFiles/' + getFileNameFromPath(imageName));
    }
  }

  public getWorkerCertificateImage(imageName: string): Observable<{ safeUrl: SafeUrl; objectUrl: string }> {
    if (!imageName) {
      return Observable.of({
        safeUrl: '',
        objectUrl: '',
      });
    } else {
      return this.getPhoto(environment.apiUrl + 'api/ext-file/workerUserCompetency/' + getFileNameFromPath(imageName));
    }
  }

  public releaseObjectUrls() {
    this.objectUrlsToRelease.forEach((objectUrl) => {
      URL.revokeObjectURL(objectUrl);
    });
    this.objectUrlsToRelease = [];
  }

  public downloadFile(objectUrl: string, fileName: string) {
    const a = document.createElement('a');
    a.setAttribute('style', 'display: none');

    a.href = objectUrl;
    a.download = fileName;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  private getPhoto(url: string): Observable<{ safeUrl: SafeUrl; objectUrl: string }> {
    return this.http.get(url, { responseType: 'blob' }).pipe(
      map((blob) => {
        const objectUrl = URL.createObjectURL(blob);
        this.objectUrlsToRelease.push(objectUrl);

        return {
          safeUrl: this.sanitizer.bypassSecurityTrustUrl(objectUrl),
          objectUrl: objectUrl,
        };
      })
    );
  }

  public uploadSignature(id: number, obj): Observable<any> {
    const fileMultipartName = 'signatureFile';

    // multipart/form-data, append each part
    const formData = new FormData();

    obj.filesArray.forEach((file: File) => {
      formData.append(fileMultipartName, file, file.name);
    });

    return this.http.patch(environment.apiUrl + `api/ext-skylark-user/${id}/signature`, formData).pipe(
      catchError((error) => {
        console.log('Error', error.error);
        if (error.error && error.error.status === 400 && error.error.errorKey === 'Infected') {
          throw new Error('The file is infected and cannot be uploaded!');
        }
        return this.handleError(error);
      })
    );
  }
}
